/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useCallback, useEffect, useMemo } from 'react'
import { View, BoxView } from '@components/atoms'
import {
	DataGrid,
	GridColDef,
	GridRenderCellParams,
	GridRowParams,
} from '@mui/x-data-grid'
import { Tabs, Tab, Chip, CircularProgress } from '@mui/material'
import { blue, orange, grey, green, blueGrey } from '@mui/material/colors'
import { useNavigate } from 'react-router-dom'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import {
	useAppDispatch,
	useAppSelector,
	inquiryMgmtActions,
	ApiGetDataStatus,
} from '@redux/index'
import { InquiryType, InquiryStatusType } from '@schemas'

dayjs.extend(utc)
dayjs.extend(timezone)

const STATUS_OPTIONS: { value: InquiryStatusType | 'all'; label: string }[] = [
	{ value: 'all', label: '전체' },
	{ value: 'new', label: '신규' },
	{ value: 'in_progress', label: '처리중' },
	{ value: 'pending', label: '대기' },
	{ value: 'resolved', label: '해결' },
	{ value: 'closed', label: '종료' },
]

const STATUS_COLOR: Record<string, string> = {
	new: blue[600],
	in_progress: orange[700],
	pending: grey[500],
	resolved: green[600],
	closed: blueGrey[700],
}

const STATUS_LABEL: Record<string, string> = {
	new: '신규',
	in_progress: '처리중',
	pending: '대기',
	resolved: '해결',
	closed: '종료',
}

const CATEG_LABEL: Record<string, string> = {
	productInquiries: '제품 문의',
	orderSupport: '주문 지원',
	feedbackSuggestions: '피드백/제안',
	collaborationOpportunities: '협업 제안',
	otherQuestions: '기타 문의',
}

const InquiryManagementPage = () => {
	const reduxDispatch = useAppDispatch()
	const navigate = useNavigate()

	const inquiryList: InquiryType[] = useAppSelector(
		state => state.inquiryMgmt.inquiryList,
	)
	const status: ApiGetDataStatus = useAppSelector(
		state => state.inquiryMgmt.inquiryListStatus,
	)
	const statusFilter: InquiryStatusType | 'all' = useAppSelector(
		state => state.inquiryMgmt.statusFilter,
	)
	const userList: { id: string; name: string }[] = useAppSelector(
		state => state.inquiryMgmt.userList,
	)

	useEffect(() => {
		reduxDispatch(inquiryMgmtActions.TRIGGER_INIT_INQUIRY_MGMT(true))
	}, [reduxDispatch])

	const userNameMap = useMemo(() => {
		const map: Record<string, string> = {}
		userList.forEach(u => {
			map[u.id] = u.name
		})
		return map
	}, [userList])

	const onTabChange = useCallback(
		(_: React.SyntheticEvent, newValue: InquiryStatusType | 'all') => {
			reduxDispatch(inquiryMgmtActions.STATUS_FILTER(newValue))
			reduxDispatch(inquiryMgmtActions.TRIGGER_INIT_INQUIRY_MGMT(true))
		},
		[reduxDispatch],
	)

	const onRowClick = useCallback(
		(params: GridRowParams<InquiryType>) => {
			reduxDispatch(inquiryMgmtActions.SELECTED_INQUIRY(params.row))
			navigate(`/main/inquiry/${params.row.id}`)
		},
		[reduxDispatch, navigate],
	)

	const columns: GridColDef<InquiryType>[] = useMemo(
		() => [
			{
				field: 'createdAt',
				headerName: '생성일',
				width: 160,
				valueFormatter: (value: string) =>
					value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '',
			},
			{
				field: 'status',
				headerName: '상태',
				width: 120,
				renderCell: (params: GridRenderCellParams) => (
					<Chip
						label={STATUS_LABEL[params.value] ?? params.value}
						size="small"
						sx={{
							backgroundColor: STATUS_COLOR[params.value] ?? grey[400],
							color: '#FFF',
							fontWeight: 600,
							fontSize: 12,
						}}
					/>
				),
			},
			{ field: 'name', headerName: '이름', width: 130 },
			{ field: 'email', headerName: '이메일', width: 200 },
			{
				field: 'categ',
				headerName: '카테고리',
				width: 160,
				valueFormatter: (value: string) => (value ? CATEG_LABEL[value] ?? value : '-'),
			},
			{
				field: 'handlerId',
				headerName: '담당자',
				width: 130,
				valueFormatter: (value: string) => (value ? userNameMap[value] ?? value : '-'),
			},
			{
				field: 'message',
				headerName: '메시지',
				flex: 1,
				minWidth: 260,
				valueFormatter: (value: string) => (value ?? '').replace(/\n/g, ' '),
			},
		],
		[userNameMap],
	)

	return (
		<View css={style}>
			<BoxView title="문의 게시판 관리">
				<View className="contentView">
					<View className="tabsView">
						<Tabs value={statusFilter} onChange={onTabChange}>
							{STATUS_OPTIONS.map(opt => (
								<Tab key={opt.value} value={opt.value} label={opt.label} />
							))}
						</Tabs>
					</View>

					{status === 'loading' ? (
						<View className="loadingView">
							<CircularProgress size={28} />
						</View>
					) : (
						<View className="gridView">
							<DataGrid
								rows={inquiryList}
								columns={columns}
								onRowClick={onRowClick}
								disableRowSelectionOnClick
								initialState={{
									pagination: { paginationModel: { pageSize: 25 } },
									sorting: {
										sortModel: [{ field: 'createdAt', sort: 'desc' }],
									},
								}}
								pageSizeOptions={[25, 50, 100]}
								sx={{
									'& .MuiDataGrid-row': { cursor: 'pointer' },
								}}
							/>
						</View>
					)}
				</View>
			</BoxView>
		</View>
	)
}

export default InquiryManagementPage

const style = css({
	width: '100%',
	height: '100%',

	'.contentView': {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		height: '100%',
		gap: '0.75rem',
	},
	'.tabsView': {
		borderBottom: `1px solid ${grey[200]}`,
		flexShrink: 0,
	},
	'.loadingView': {
		display: 'flex',
		justifyContent: 'center',
		padding: '3rem 0',
	},
	'.gridView': {
		flex: 1,
		width: '100%',
		minHeight: '30rem',
	},
})
